import { useState, useEffect, useMemo, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import MultiSelect from '../components/common/MultiSelect.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import ScatterChart from '../components/charts/ScatterChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell, IdCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, formatSavingStr,
  buildBaseCostKpis, exportCSV,
  getScopedItem, setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';

import RecommendationApiService from '../services/RecommendationApiService';
import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import { useAuth } from '../state/AuthContext';

// ── Tabs ─────────────────────────────────────────────────────

const subTabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'Azure Recommendations' },
];

// type sent to /api/azure/recommendations, keyed by sub-tab index
const TAB_TYPES = ['COMBINED_MANAGED_DISK_SNAPSHOT', 'CUSTOM_MANAGED_DISK_SNAPSHOT', 'MANAGED_DISK_SNAPSHOT'];

const STATE_COLORS = { Orphaned: '#ef4444', Aged: '#f57c00', 'In-policy': '#2ea66f' };
const DELETE_RECS  = ['DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE'];

// ── Rec helpers ──────────────────────────────────────────────

const resolveSnapshotRec = (recType) => {
  const t = (recType ?? '').toUpperCase().trim();
  let recPillClass = 'rec-ok';
  if (DELETE_RECS.includes(t))                                                        recPillClass = 'rec-id';
  else if (t === 'PRUNE')                                                             recPillClass = 'rec-rs';
  else if (['INCREMENTAL', 'MOVE', 'MOVETOINCREMENTAL', 'MOVE_TO_INCREMENTAL'].includes(t)) recPillClass = 'rec-tier';
  return { recType: t || 'KEEP', recPillClass };
};

const notNA = (v) => (v != null && String(v).trim() !== '' && String(v).trim().toUpperCase() !== 'NA') ? v : null;

const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

// Age in whole days from an ISO timestamp.
const ageFromCreated = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

// ── Row Mapper ───────────────────────────────────────────────
// All three tabs share one item shape; source-specific keys (name/snapshotName,
// resourceLocation, sourceDiskName, startDays…) are read defensively.

const toSnapshotRow = (item) => {
  const current     = parseCost(item.currentMonthlyPriceValue);
  const recommended = parseCost(item.recommendedMonthlyPriceValue);
  const saving      = parseCost(item.savingCost ?? Math.max(0, current - recommended));

  const rawRec = item.recommendationType ?? (saving > 0 ? 'DELETE' : 'KEEP');
  const { recType, recPillClass } = resolveSnapshotRec(rawRec);

  // Show backend detail exactly; NA / absent falls through, finally "—".
  const remark =
    notNA(item.remark) ??
    notNA(item.recommendationSubCategory) ??
    '—';

  const sourceDiskExists = item.sourceDiskExists === true;
  const ageDays = ageFromCreated(item.timeCreated) ?? toNum(item.startDays);

  // Derive a lifecycle state used by the charts / State column.
  const isOrphaned = !sourceDiskExists || DELETE_RECS.includes(recType);
  const isAged     = !isOrphaned && ['OVER_ONE_YEAR', 'SIX_MONTHS_TO_ONE_YEAR'].includes(item.startTimeEnum);
  const snapshotState = isOrphaned ? 'Orphaned' : isAged ? 'Aged' : 'In-policy';

  return {
    id: item.id,
    snapshotName: item.snapshotName ?? item.name ?? '—',
    location: item.location ?? item.resourceLocation ?? '—',
    resourceGroup: item.resourceGroup ?? '—',
    sourceDiskName: item.sourceDiskName ?? '—',
    sourceDiskExists,
    diskType: item.diskType ?? '—',
    diskState: item.diskState ?? '—',
    sizeGB: toNum(item.diskSizeGB),
    osType: item.osType ?? '—',
    sku: item.skuName ?? '—',
    skuTier: item.skuTier ?? '—',
    impact: item.impact ?? '—',
    provisioningState: item.provisioningState ?? '—',
    billedGBMonth: toNum(item.billedGBMonth),
    startTimeEnum: item.startTimeEnum ?? '',
    timeCreated: item.timeCreated ?? null,
    ageDays,
    snapshotState,
    cost: `$${current.toFixed(2)}`,
    costValue: current,
    recommendedCost: `$${recommended.toFixed(2)}`,
    recommendedCostValue: recommended,
    recommendationRemark: remark,
    recType,
    recPillClass,
    saving: formatSavingStr(saving),
    savingValue: saving,
    recommendedBy: item.recommendedBy ?? '—',
    actionLabel: 'Assist with AI',
    actionClass: 'mini-btn ai-assist',
  };
};

const mapRows = (data) => (data ?? []).map(toSnapshotRow);

// ── KPI ──────────────────────────────────────────────────────

const buildKpiData = (totalMonthlyCost, rows) => {
  const rowCurrent = rows.reduce((s, r) => s + r.costValue, 0);
  const rowRec     = rows.reduce((s, r) => s + r.recommendedCostValue, 0);
  const rowSaving  = rows.reduce((s, r) => s + r.savingValue, 0);

  const current     = totalMonthlyCost != null ? parseCost(totalMonthlyCost.currentCost)     : rowCurrent;
  const recommended = totalMonthlyCost != null ? parseCost(totalMonthlyCost.recommendedCost) : rowRec;
  const saving      = totalMonthlyCost != null ? parseCost(totalMonthlyCost.savingCost)      : rowSaving;

  const totalSizeGB = rows.reduce((s, r) => s + (r.sizeGB || 0), 0);
  const regionCount = new Set(rows.map((r) => r.location).filter((l) => l && l !== '—')).size;
  const orphanRows  = rows.filter((r) => r.snapshotState === 'Orphaned');
  const orphanCost  = orphanRows.reduce((s, r) => s + r.costValue, 0);

  return buildBaseCostKpis({
    countTitle: 'Total Snapshots',
    countValue: rows.length,
    countSubtitle: `${totalSizeGB.toLocaleString()} GB · across ${regionCount} region${regionCount !== 1 ? 's' : ''}`,
    current, saving, recommended,
    kpi4Title: 'Orphaned Snapshots',
    kpi4Value: `${orphanRows.length}`,
    kpi4Subtitle: `source disk deleted · $${orphanCost.toFixed(2)}/mo`,
  });
};

// ── Charts ────────────────────────────────────────────────────

// Scatter: age (days) vs size (GiB), grouped by lifecycle state.
const buildAgeSizeScatter = (rows) => {
  const groups = { Orphaned: [], Aged: [], 'In-policy': [] };
  rows.forEach((r) => {
    if (r.ageDays == null || r.sizeGB == null) return;
    (groups[r.snapshotState] ?? groups['In-policy']).push({ x: r.ageDays, y: r.sizeGB, name: r.snapshotName });
  });
  const datasets = Object.entries(groups)
    .filter(([, pts]) => pts.length)
    .map(([state, pts]) => ({
      label: state,
      data: pts,
      backgroundColor: STATE_COLORS[state],
      pointRadius: 7,
      pointHoverRadius: 9,
    }));
  if (!datasets.length) return null;
  return { datasets };
};

// Donut: storage (GiB) by lifecycle state, count fallback when no size.
const buildStateDonut = (rows) => {
  if (!rows.length) return null;
  const hasSize = rows.some((r) => (r.sizeGB || 0) > 0);
  const buckets = { Orphaned: 0, Aged: 0, 'In-policy': 0 };
  rows.forEach((r) => { buckets[r.snapshotState] += hasSize ? (r.sizeGB || 0) : 1; });
  const labels = Object.keys(buckets).filter((k) => buckets[k] > 0);
  if (!labels.length) return null;
  return {
    labels,
    datasets: [{
      data: labels.map((k) => parseFloat(buckets[k].toFixed(2))),
      backgroundColor: labels.map((k) => STATE_COLORS[k]),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };
};

// Stacked bar: monthly snapshot cost per age band, split by lifecycle state.
// Reveals where the retention buildup sits — old orphaned snapshots that keep billing.
const AGE_BANDS = [
  { key: 'ZERO_TO_SIX_MONTHS',     label: '0–6 months' },
  { key: 'SIX_MONTHS_TO_ONE_YEAR', label: '6–12 months' },
  { key: 'OVER_ONE_YEAR',          label: '1 year+' },
];

const buildCostByAgeBand = (rows) => {
  if (!rows.length) return null;
  const hasCost = rows.some((r) => r.costValue > 0);
  const states  = ['Orphaned', 'Aged', 'In-policy'];

  const grid = {};
  AGE_BANDS.forEach((b) => { grid[b.key] = { Orphaned: 0, Aged: 0, 'In-policy': 0 }; });
  rows.forEach((r) => {
    const band = AGE_BANDS.find((b) => b.key === r.startTimeEnum)?.key ?? 'ZERO_TO_SIX_MONTHS';
    grid[band][r.snapshotState] += hasCost ? (r.costValue || 0) : 1;
  });

  const activeStates = states.filter((st) => AGE_BANDS.some((b) => grid[b.key][st] > 0));
  if (!activeStates.length) return null;

  return {
    _hasCost: hasCost,
    labels: AGE_BANDS.map((b) => b.label),
    datasets: activeStates.map((st) => ({
      label: st,
      data: AGE_BANDS.map((b) => parseFloat(grid[b.key][st].toFixed(2))),
      backgroundColor: STATE_COLORS[st],
      borderRadius: 4,
      barPercentage: 0.6,
    })),
  };
};

const buildSavingsByAction = (rows) => {
  const buckets = { 'Delete orphaned': 0, 'Prune past retention': 0, 'Full → Incremental': 0 };
  rows.forEach((r) => {
    const s = r.savingValue;
    if (s <= 0) return;
    if (DELETE_RECS.includes(r.recType)) buckets['Delete orphaned'] += s;
    else if (r.recType === 'PRUNE') buckets['Prune past retention'] += s;
    else if (['INCREMENTAL', 'MOVE', 'MOVETOINCREMENTAL', 'MOVE_TO_INCREMENTAL'].includes(r.recType)) buckets['Full → Incremental'] += s;
    else buckets['Delete orphaned'] += s;
  });
  if (!Object.values(buckets).some((v) => v > 0)) return null;
  return {
    labels: Object.keys(buckets),
    datasets: [{
      label: 'Saving ($)',
      data: Object.values(buckets).map((v) => parseFloat(v.toFixed(2))),
      backgroundColor: ['#ef4444', '#f57c00', '#2ea66f'],
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

const buildInsightActions = (rows) => {
  const withSaving = rows
    .filter((r) => r.savingValue > 0)
    .sort((a, b) => b.savingValue - a.savingValue);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingValue, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →'; let actionClass = 'mini-btn';
    if (DELETE_RECS.includes(r.recType)) { actionLabel = 'Delete →'; actionClass = 'mini-btn ghost'; }
    else if (r.recType === 'PRUNE') actionLabel = 'Prune →';
    else if (['INCREMENTAL', 'MOVE', 'MOVETOINCREMENTAL', 'MOVE_TO_INCREMENTAL'].includes(r.recType)) actionLabel = 'Convert →';
    return {
      id: i + 1,
      title: r.snapshotName || r.id,
      amount: r.saving,
      desc: `${r.diskType} · ${r.sizeGB ?? '—'} GB · ${r.location}`,
      actionLabel,
      actionClass,
      row: r,
    };
  });
  return { actions, totalSaving };
};

// ── Cells ─────────────────────────────────────────────────────

const StateCell = ({ row }) => {
  const s = (row.snapshotState ?? '').toLowerCase();
  const cls = s === 'orphaned' ? 'st-stopped' : s === 'aged' ? 'st-warn' : 'st-running';
  return <span className={`status-pill ${cls}`}>{row.snapshotState}</span>;
};

// ── Table Columns ─────────────────────────────────────────────

const snapshotColumns = [
  { key: 'id',            label: 'Id', render: (row) => <IdCell row={row} /> },
  { key: 'snapshotName',  label: 'Snapshot Name' },
  { key: 'location',      label: 'Location' },
  { key: 'resourceGroup', label: 'Resource Group' },
  {
    key: 'sourceDiskName', label: 'Source Disk',
    render: (row) => row.sourceDiskExists && row.sourceDiskName !== '—'
      ? <span title={row.sourceDiskName}>{row.sourceDiskName}</span>
      : <span style={{ color: 'var(--muted)' }}>— (deleted)</span>,
  },
  {
    key: 'sku', label: 'SKU',
    render: (row) => (row.skuTier && row.skuTier !== '—') ? `${row.sku} (${row.skuTier})` : row.sku,
  },
  { key: 'diskType', label: 'Disk Type' },
  { key: 'sizeGB',   label: 'Size (GB)', className: 'num', render: (row) => row.sizeGB != null ? row.sizeGB : '—' },
  { key: 'osType',   label: 'OS' },
  { key: 'snapshotState', label: 'State', render: (row) => <StateCell row={row} /> },
  {
    key: 'ageDays', label: 'Age', className: 'num',
    render: (row) => row.ageDays != null ? `${row.ageDays} d` : '—',
  },
  { key: 'cost',                 label: 'Cost / Mo',         className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation',    render: (row) => <RecCell row={row} /> },
  { key: 'saving',               label: 'Saving / Mo',       className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost',      label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
  {
    key: 'action', label: 'Action',
    render: (row) => (
      <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>
        {row.actionLabel}
      </button>
    ),
  },
];

// ── Main Page ─────────────────────────────────────────────────

const AzureManagedDiskSnapshotOptimization = () => {
  const { selectedAccount } = useAuth();

  const [activeTab, setActiveTab] = useState(0);

  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom,   setIsLoadingCustom]   = useState(false);
  const [isLoadingAzure,    setIsLoadingAzure]    = useState(false);

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows,   setCustomRows]   = useState([]);
  const [azureRows,    setAzureRows]    = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi,   setCustomKpi]   = useState([]);
  const [azureKpi,    setAzureKpi]    = useState([]);

  const [scatterData,  setScatterData]  = useState(null);
  const [stateData,    setStateData]    = useState(null);
  const [ageBandData,  setAgeBandData]  = useState(null);
  const [savingsData,  setSavingsData]  = useState(null);
  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal,   setInsightTotal]   = useState(0);

  const [tagList,        setTagList]        = useState([]);
  const [filterList,     setFilterList]     = useState({ tag: '', values: [] });
  const [locationFilter, setLocationFilter] = useState('');
  const [stateFilter,    setStateFilter]    = useState('');
  const [diskTypeFilter, setDiskTypeFilter] = useState('');
  const [ageFilter,      setAgeFilter]      = useState('');

  const [aiOpen,   setAiOpen]   = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData,   setAiData]   = useState(null);

  const getUserId = () => {
    const raw = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (!raw) return null;
    try { return JSON.parse(raw)?.userId ?? null; } catch { return null; }
  };

  const handleDetailsClick = (row) => {
    setAiData({
      snapshotId: row.id, snapshotName: row.snapshotName, location: row.location,
      resourceGroup: row.resourceGroup, sourceDisk: row.sourceDiskName, sourceDiskExists: row.sourceDiskExists,
      diskType: row.diskType, sizeGB: row.sizeGB, osType: row.osType, sku: row.sku,
      state: row.snapshotState, ageDays: row.ageDays,
      currentMonthlyCost: row.cost, recommendation: row.recType, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the disk snapshot data provided, give a concise insight directly to the ' +
      'user — mention orphaned or aged snapshots, cost savings opportunities, and what action they should take. ' +
      'Keep it conversational and to the point. Do not mention field names or JSON.'
    );
    setAiOpen(true);
  };

  const fetchTagList = () => {
    if (!selectedAccount) return;
    setTagList([]);
    if (filterList.tag !== '' || filterList.values.length > 0) setFilterList({ tag: '', values: [] });
    InventoryApiService.getAzureFilterTagList(
      APIConstants.ENDPOINT_AZURE_FILTER_TAG,
      { type: 'MANAGED_DISK_SNAPSHOT', userAccountId: selectedAccount }
    )
      .then((res) => setTagList(res?.data ?? []))
      .catch(() => {});
  };

  const fetchedTabsRef = useRef(new Set());

  const fetchCombinedData = (userId) => {
    setIsLoadingCombined(true);
    setCombinedRows([]);
    RecommendationApiService.getAzureData({ userId, type: TAB_TYPES[0], userAccountId: selectedAccount })
      .then((res) => {
        const rows = mapRows(res?.data);
        setCombinedRows(rows);
        setCombinedKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching combined snapshot recommendations', err))
      .finally(() => setIsLoadingCombined(false));
  };

  const fetchCustomData = (userId) => {
    setIsLoadingCustom(true);
    setCustomRows([]);
    RecommendationApiService.getAzureData({
      userId, type: TAB_TYPES[1], userAccountId: selectedAccount,
      filterKey: filterList.tag || undefined,
      filterValues: filterList.values.length > 0 ? filterList.values : undefined,
    })
      .then((res) => {
        const rows = mapRows(res?.data);
        setCustomRows(rows);
        setCustomKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching custom snapshot recommendations', err))
      .finally(() => setIsLoadingCustom(false));
  };

  const fetchAzureData = (userId) => {
    setIsLoadingAzure(true);
    setAzureRows([]);
    RecommendationApiService.getAzureData({ userId, type: TAB_TYPES[2], userAccountId: selectedAccount })
      .then((res) => {
        const rows = mapRows(res?.data);
        setAzureRows(rows);
        setAzureKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((err) => console.error('Error fetching azure snapshot recommendations', err))
      .finally(() => setIsLoadingAzure(false));
  };

  // Lazy fetch: only hits the API for a tab the first time it is opened.
  const fetchTabData = (tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    const userId = getUserId();
    if (!userId) return;

    if (tabIndex === 0) fetchCombinedData(userId);
    else if (tabIndex === 1) fetchCustomData(userId);
    else if (tabIndex === 2) fetchAzureData(userId);
  };

  const handleTabChange = (i) => {
    setActiveTab(i);
    fetchTabData(i);
  };

  const handleRefresh = () => {
    fetchedTabsRef.current.delete(activeTab);
    fetchTabData(activeTab, true);
    fetchTagList();
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag')    { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  useEffect(() => {
    if (selectedAccount) {
      setFilterList({ tag: '', values: [] });
      fetchedTabsRef.current = new Set();
      setActiveTab(0);
      fetchTabData(0);
      fetchTagList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  // CloudsAnalytics (custom) tag filters are server-side — refetch only while its tab is active.
  useEffect(() => {
    if (activeTab !== 1 || !selectedAccount) return;
    if (filterList.tag === '' || filterList.values.length > 0) {
      const userId = getUserId();
      if (userId) {
        fetchedTabsRef.current.add(1);
        fetchCustomData(userId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList]);

  const activeKpi       = activeTab === 0 ? combinedKpi       : activeTab === 1 ? customKpi       : azureKpi;
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAzure;
  const baseRows        = activeTab === 0 ? combinedRows      : activeTab === 1 ? customRows      : azureRows;

  const locations  = useMemo(() => [...new Set(baseRows.map((r) => r.location).filter((v) => v && v !== '—'))].sort(), [baseRows]);
  const diskTypes  = useMemo(() => [...new Set(baseRows.map((r) => r.diskType).filter((v) => v && v !== '—'))].sort(), [baseRows]);

  const filteredRows = useMemo(() => {
    let rows = baseRows;
    if (locationFilter) rows = rows.filter((r) => r.location === locationFilter);
    if (stateFilter)    rows = rows.filter((r) => r.snapshotState === stateFilter);
    if (diskTypeFilter) rows = rows.filter((r) => r.diskType === diskTypeFilter);
    if (ageFilter === '90+')  rows = rows.filter((r) => (r.ageDays ?? 0) >= 90);
    if (ageFilter === '365+') rows = rows.filter((r) => (r.ageDays ?? 0) >= 365);
    return rows;
  }, [baseRows, locationFilter, stateFilter, diskTypeFilter, ageFilter]);

  const activeRows = filteredRows.map((row) => ({ ...row, onAction: handleDetailsClick }));

  useEffect(() => {
    if (filteredRows.length > 0) {
      setScatterData(buildAgeSizeScatter(filteredRows));
      setStateData(buildStateDonut(filteredRows));
      setAgeBandData(buildCostByAgeBand(filteredRows));
      setSavingsData(buildSavingsByAction(filteredRows));
      const { actions, totalSaving } = buildInsightActions(filteredRows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setScatterData(null); setStateData(null); setAgeBandData(null); setSavingsData(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [filteredRows]);

  // Keep AI chatbot context in sync with what's on screen
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Managed Disk Snapshot Optimization',
      kpi: activeKpi,
      snapshotRecommendations: filteredRows,
    });
  }, [filteredRows, activeKpi]);

  const handleExport = () =>
    exportCSV(activeRows, snapshotColumns.filter((c) => c.key !== 'action'), `azure-disk-snapshot-tab${activeTab}.csv`);

  const clearLocalFilters = () => {
    setLocationFilter(''); setStateFilter(''); setDiskTypeFilter(''); setAgeFilter('');
    setFilterList({ tag: '', values: [] });
  };

  const showClear = !!(filterList.tag || locationFilter || stateFilter || diskTypeFilter || ageFilter);

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span>{' '}
        <strong>Managed Disk Snapshot</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Disk Snapshot Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{baseRows.length} snapshots</span>}
          </h1>
          <div className="meta">
            Azure Managed Disk Snapshots · delete orphaned, prune aged, convert full to incremental
          </div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport}>⬇ Export</button>
          <button className="btn ghost" onClick={handleRefresh}>↻ Refresh</button>
        </div>
      </div>

      <KpiRow kpiData={activeKpi} loading={isLoadingActive} />

      {/* Charts */}
      <div className="grid-3" style={{ marginTop: '4px' }}>
        <Card className="first" title="Snapshot age vs size" subtitle="Old, large snapshots are deletion candidates">
          <ScatterChart
            data={scatterData}
            loading={isLoadingActive}
            xLabel="Age (days)"
            yLabel="Snapshot size (GiB)"
            tooltipCallback={(c) => `${c.raw.name ?? c.dataset.label}: ${c.parsed.x} d · ${c.parsed.y} GiB`}
          />
        </Card>
        <Card className="first" title="Storage by snapshot state" subtitle="Orphaned / Aged / In-policy">
          <DonutChart data={stateData} loading={isLoadingActive} />
        </Card>
        <TopActionsCard
          title="Top snapshot actions"
          actions={insightActions}
          total={insightTotal}
          loading={isLoadingActive}
          entityName="snapshots"
          onAction={(item) => item.row && handleDetailsClick(item.row)}
        />
      </div>

      {/* Sub-tabs */}
      <div className="sub-tabs">
        {subTabs.map((t, i) => (
          <div key={i} className={`sub-tab${activeTab === i ? ' active' : ''}`} onClick={() => handleTabChange(i)}>
            {t.label}
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <div className="field">
          <label>Filter Tag</label>
          <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
            <option value="">None</option>
            {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Filter Tag Values</label>
          <MultiSelect
            options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
            value={filterList.values}
            onChange={(vals) => handleFilterChange('values', vals)}
            disabled={!filterList.tag}
          />
        </div>
        <div className="field">
          <label>Location</label>
          <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="field">
          <label>State</label>
          <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
            <option value="">All</option>
            <option value="Orphaned">Orphaned</option>
            <option value="Aged">Aged</option>
            <option value="In-policy">In-policy</option>
          </select>
        </div>
        <div className="field">
          <label>Disk Type</label>
          <select value={diskTypeFilter} onChange={(e) => setDiskTypeFilter(e.target.value)}>
            <option value="">All</option>
            {diskTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Age</label>
          <select value={ageFilter} onChange={(e) => setAgeFilter(e.target.value)}>
            <option value="">All</option>
            <option value="90+">{'> 90 days'}</option>
            <option value="365+">{'> 1 year'}</option>
          </select>
        </div>
        <div className="spacer" />
        {showClear && <button className="btn ghost" onClick={clearLocalFilters}>✕ Clear</button>}
        <button className="btn ghost" onClick={handleRefresh}>↻ Refresh</button>
      </div>

      <Table
        rows={activeRows}
        columns={snapshotColumns}
        loading={isLoadingActive}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No managed disk snapshot recommendations"
      />

      {/* Bottom charts */}
      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Snapshot cost by age band" subtitle="Retention buildup — orphaned vs aged vs in-policy">
          <HorizontalBarChart
            data={ageBandData}
            loading={isLoadingActive}
            stacked
            xTickCallback={ageBandData && ageBandData._hasCost === false ? (v) => v : undefined}
          />
        </Card>
        <Card className="first" title="Recommended savings by action" subtitle="Where the savings come from">
          <HorizontalBarChart data={savingsData} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default AzureManagedDiskSnapshotOptimization;
